import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link as LinkRouter } from "react-router-dom";

import { useSnackbar } from "notistack";
import { useFlags } from "flagsmith/react";

import { useTheme } from "@material-ui/core/styles";
import {
  Box,
  Button,
  LinearProgress,
  Link,
  Typography,
  useMediaQuery,
} from "@material-ui/core";
import Grid from "@material-ui/core/Grid";

import LoadingCardTitleMobile from "@components/CardTitle/LoadingCardTitleMobile";
import CarouselBasic, { CarouselBasicItem } from "@components/CarouselBasic";
import { TypesPermissions } from "@components/Routes/TypesPermissions";
import { LegacyTypesPermissions } from "@components/Routes/legacyTypesPermissions";
import { getAdaptivePermissionMatcher } from "@utils/permissions/getAdaptivePermissionMatcher";
import { useSystemModulesAuthFlag } from "@hooks/useSystemModulesAuthFlag";
import LoadingCardOrder from "@components/CardOrder/LoadingCardOrder";
import LoadingCardTitle from "@components/CardTitle/LoadingCardTitle";
import CardTitleMobile from "@components/CardTitleMobile";
import CardOrder from "@components/CardOrder";
import CardTitle from "@components/CardTitle";
import Modal from "@components/Modal";

import { CactusIcon } from "@components/Icons";

import ModalLastOrders from "@components/ModalLastOrders";
import { useHomeUserContext } from "@context/HomeUserContext";
import { useIoCContext } from "@context/IoCContext/IoCContext";
import { Types } from "@ioc/types";

import { IGetLastOrdersService } from "@modules/lastOrders/models/IGetLastOrdersService";
import { IGetCardTitleService } from "@modules/cardTitles/models/IGetCardTitleService";
import { ICommunicationService } from "@modules/communications/model/ICommunication";
import { IGetLastOrdersDTO } from "@modules/lastOrders/dtos/IGetLastOrdersDTO";
import { IGetCardTitleDTO } from "@modules/cardTitles/dtos/IGetCardTitleDTO";

import newsImg from "../../../assets/campanhaFeriasTurbinadasOpaca.png";
import logoAtem from "../../../assets/logo-atem.svg";
import Layout from "../../../components/Layout";
import ROUTES from "../../../config/routes";

import { useAuth } from "@context/auth/AuthContext";
import AppError from "@utils/AppError";

import HomeNotificationContainer from "./HomeNotificationContainer";
import useStylesHome from "./home.styles";
import HomeHeader from "./HomeHeader";

const HomePage = () => {
  const { permissionSet } = useAuth();
  const useSystemModulesAuth = useSystemModulesAuthFlag();
  const conditionAuthGuardTitle = permissionSet.SYSTEM_MODULES.some(
    getAdaptivePermissionMatcher(
      TypesPermissions.TITLES,
      LegacyTypesPermissions.APPOINTMENTS_TITLE,
      useSystemModulesAuth
    )
  );

  const classes = useStylesHome();
  const { enqueueSnackbar } = useSnackbar();

  const flag_modo_user_communique = useFlags([
    "user_home_see_banner_communique",
  ]);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.only("xs"));
  const iocContext = useIoCContext();
  const { documentList } = useHomeUserContext();
  const {
    isShowTableLastOrder,
    setIsShowTableLastOrder,
  } = useHomeUserContext();

  const [lastOrders, setLastOrders] = useState<IGetLastOrdersDTO[]>([]);
  const [cardTitles, setCardTitles] = useState<IGetCardTitleDTO[]>([]);

  const [isLoadingOrders, setIsLoadingOrders] = useState(true);
  const [isLoadingTitles, setIsLoadingTitles] = useState(true);
  const [openModalLastOrders, setOpenModalLastOrders] = React.useState(false);
  const [indexModalSelect, setIndexModalSelect] = React.useState(-1);
  const [openComunication, setOpenComunication] = useState(false);
  const [
    imgComunication,
    setImgComunication,
  ] = useState<CarouselBasicItem | null>(null);

  const [loadingComunication, setLoadingComunication] = useState<boolean>(
    false
  );
  const [communicateds, setCommunicateds] = useState<CarouselBasicItem[]>([]);

  const hasCommunication = communicateds.length > 0;

  let itemSelected = documentList.filter((item) => item.isSelected)[0];
  let documentItemSelect = itemSelected?.cnpj;

  const getLastOrdersService = iocContext.serviceContainer.get<
    IGetLastOrdersService
  >(Types.LastOrders.IGetLastOrdersService);
  const getCardTitleService = iocContext.serviceContainer.get<
    IGetCardTitleService
  >(Types.CardTitle.IGetCardTitleService);

  const fetchAllComunication = useCallback(async () => {
    try {
      setLoadingComunication(true);
      const comunicationdService = iocContext.serviceContainer.get<
        ICommunicationService
      >(Types.Communication.ICommunicationService);
      const payload = await comunicationdService.findAll([documentItemSelect]);
      const communication = payload.map((curr) => ({ imageUrl: curr.path }));
      setCommunicateds(communication);
    } catch (error) {
      console.error(error);
      if (error instanceof AppError) {
        console.error(error.message);
      }
      setCommunicateds([]);
    } finally {
      setLoadingComunication(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentItemSelect]);

  const getLastOrders = useCallback(async () => {
    try {
      if (isShowTableLastOrder) {
        setIsLoadingOrders(true);
      }
      const data = await getLastOrdersService.execute(documentItemSelect);

      setLastOrders(data);
    } catch (error) {
      console.info("Ocorreu um erro ao buscar os últimos pedidos");
    } finally {
      setIsLoadingOrders(false);
    }
  }, [isShowTableLastOrder, getLastOrdersService, documentItemSelect]);

  const getCardTitle = useCallback(async () => {
    try {
      setIsLoadingTitles(true);
      const data = await getCardTitleService.execute(documentItemSelect);
      setCardTitles(data);
    } catch (error) {
      enqueueSnackbar("Ocorreu um erro ao buscar os últimos títulos", {
        variant: "error",
      });
    } finally {
      setIsLoadingTitles(false);
    }
  }, [getCardTitleService, documentItemSelect, enqueueSnackbar]);

  useEffect(() => {
    getLastOrders();
    getCardTitle();
    fetchAllComunication();
  }, [getLastOrders, getCardTitle, fetchAllComunication]);

  const getLastOrdersRef = useRef<any>();
  useEffect(() => {
    getLastOrdersRef.current = getLastOrders;

    if (!isShowTableLastOrder) {
      const interval = setInterval(() => {
        if (getLastOrdersRef.current) {
          getLastOrdersRef.current();
        }
      }, 5000);

      return () => clearInterval(interval);
    }
  }, [getLastOrders, isShowTableLastOrder]);

  const handleOpenModalLastOrders = (index: number) => (event) => {
    setIndexModalSelect(index);
    setOpenModalLastOrders(true);
  };

  const handleCloseModalLastOrders = () => {
    setOpenModalLastOrders(false);
    setIsShowTableLastOrder(true);
  };

  const handleOpenModal = (data: CarouselBasicItem) => {
    setImgComunication(data);
    setOpenComunication(true);
  };
  const handleCloseModal = () => setOpenComunication(false);

  const ImgCommunication = () => {
    return flag_modo_user_communique.user_home_see_banner_communique.enabled ? (
      <CommunicationLayer />
    ) : (
      <img
        src={newsImg}
        alt="Imagem Aviso Atem"
        width={"100%"}
        height={"390px"}
        style={{ marginTop: "5px", cursor: "pointer" }}
      />
    );
  };

  const CommunicationLayer: React.FC = () => {
    return (
      <div
        className={"card-comunicado"}
        style={{ border: "0px solid red", background: "#F5F5F5" }}
      >
        {loadingComunication ? (
          <LinearProgress />
        ) : hasCommunication ? (
          <CarouselBasic
            items={communicateds}
            handlePaperClick={handleOpenModal}
          />
        ) : (
          <div></div>
        )}
      </div>
    );
  };

  return (
    <Layout disableFooter>
      {/* Sistema de notificacao em desenvolvimento */}
      <HomeNotificationContainer />

      <HomeHeader isMobile={isMobile} classes={classes} />

      <Box className={classes.containerSection}>
        <Grid
          container
          justifyContent="center"
          alignItems={hasCommunication ? "flex-start" : "center"}
          spacing={isMobile ? 0 : 4}
        >
          <Grid item xs={12} md={hasCommunication ? 9 : 12}>
            <Box className={isMobile ? classes.paperMobile : classes.paper}>
              <Typography
                className={
                  isMobile ? classes.titleSectionMobile : classes.titleSection
                }
                style={{ marginBottom: "20px" }}
              >
                Últimos pedidos
              </Typography>

              {isLoadingOrders && <LoadingCardOrder />}

              {lastOrders.length > 0 &&
                !isLoadingOrders &&
                lastOrders.map((value, index) => (
                  <Box key={index} style={{ marginBottom: 24 }}>
                    <Button
                      type="button"
                      onClick={handleOpenModalLastOrders(index)}
                      className={classes.button}
                    >
                      <CardOrder
                        key={index}
                        numberOrder={value.numberOrder}
                        implantationDate={value.implantationDate}
                        deliveryDate={value.deliveryDate}
                        companyName={value.companyName}
                        vehiclePlate={value.vehiclePlate}
                        statusAtem={value.statusAtem}
                        driverName={value.driverName}
                      />
                    </Button>
                  </Box>
                ))}

              {!lastOrders.length && !isLoadingOrders && (
                <>
                  <Grid
                    container
                    justifyContent="center"
                    alignItems="center"
                    style={{
                      paddingTop:
                        hasCommunication && !isMobile
                          ? "100px"
                          : !hasCommunication && !lastOrders.length
                          ? "30px"
                          : "0px",
                    }}
                  >
                    <CactusIcon
                      style={{
                        width: isMobile ? "50px" : "10rem",
                        height: isMobile ? "50px" : "10rem",
                      }}
                    />
                  </Grid>

                  <Grid container justifyContent="center" alignItems="center">
                    <Typography
                      style={{
                        color: "#3E3D3D",
                        fontFamily: "DM Sans",
                        fontSize: isMobile ? "12px" : "16px",
                        fontStyle: "normal",
                        fontWeight: "400",
                        lineHeight: "normal",
                        marginTop: isMobile
                          ? "20px"
                          : hasCommunication
                          ? "30px"
                          : "40px",
                        marginBottom: isMobile ? "30px" : "40px",
                        textAlign: "center",
                      }}
                    >
                      Nada para exibir nos últimos pedidos do CNPJ selecionado.
                    </Typography>
                  </Grid>
                </>
              )}

              <ModalLastOrders
                open={openModalLastOrders}
                handleOnClose={handleCloseModalLastOrders}
                lastOrder={lastOrders[indexModalSelect]}
              />
              <Box
                style={{
                  textAlign: isMobile
                    ? "center"
                    : !isMobile && !lastOrders.length
                    ? "center"
                    : "right",
                  marginBottom: isMobile ? "30px" : "",
                  marginTop: isMobile ? "0px" : "",
                }}
              >
                <Link
                  component={LinkRouter}
                  to={ROUTES.USER_ROUTES.MY_ORDERS}
                  className={classes.link}
                  underline="none"
                >
                  <Typography className={classes.linkOrdersUnderTable}>
                    Ver todos os pedidos
                  </Typography>
                </Link>
              </Box>
            </Box>

            {!conditionAuthGuardTitle ? (
              <></>
            ) : isMobile ? (
              isLoadingTitles ? (
                <LoadingCardTitleMobile />
              ) : (
                <CardTitleMobile title="Títulos" cards={cardTitles} />
              )
            ) : (
              <>
                <Box
                  width={isMobile ? "97%" : !hasCommunication ? "99%" : "97.5%"}
                >
                  <Box
                    className={isMobile ? classes.paperMobile : classes.paper}
                  >
                    <Typography
                      className={
                        isMobile
                          ? classes.titleSectionMobile
                          : classes.titleSection
                      }
                      style={{ marginBottom: "20px" }}
                    >
                      Títulos
                    </Typography>
                  </Box>
                </Box>
                <Box className={isMobile ? classes.paperMobile : classes.paper}>
                  {cardTitles.length ? (
                    <>
                      <Box className={classes.cardTitle}>
                        {isLoadingTitles ? (
                          <LoadingCardTitle />
                        ) : (
                          cardTitles.map((value, index) => (
                            <CardTitle
                              key={index}
                              customerName={value.customerName}
                              statusDoc={value.statusDoc}
                              value={value.value}
                              reference={value.reference}
                            />
                          ))
                        )}
                      </Box>
                      <Box
                        style={{
                          textAlign: "right",
                          width: !hasCommunication ? "100%" : "99.6%",
                        }}
                      >
                        <Link
                          component={LinkRouter}
                          to={ROUTES.USER_ROUTES.APPOINTMENT_TITLE}
                          className={classes.link}
                          underline="none"
                        >
                          <Typography className={classes.linkTitlesUnderTable}>
                            Ver todos os títulos
                          </Typography>
                        </Link>
                      </Box>
                    </>
                  ) : (
                    !isLoadingTitles && (
                      <Box style={{ textAlign: "left" }}>
                        <Typography className={classes.msgEmptyTitles}>
                          Você não possui títulos em aberto.
                        </Typography>
                      </Box>
                    )
                  )}
                </Box>
              </>
            )}
          </Grid>

          {hasCommunication && (
            <Box width={"23%"} display={isMobile ? "none" : "block"}>
              <Box className={isMobile ? classes.paperMobile : classes.paper}>
                <Typography
                  className={
                    isMobile ? classes.titleSectionMobile : classes.titleSection
                  }
                  style={{
                    marginBottom: "20px",
                    marginTop: "21px",
                    textAlign: "center",
                  }}
                >
                  Comunicados
                </Typography>
                <ImgCommunication />
              </Box>
            </Box>
          )}
        </Grid>

        {isMobile &&
        flag_modo_user_communique.user_home_see_banner_communique.enabled ? (
          <CommunicationLayer />
        ) : (
          <>
            {isMobile && (
              <Box sx={{ mt: 4 }}>
                <Box className={isMobile ? classes.paperMobile : classes.paper}>
                  <Typography
                    className={classes.titleSection}
                    style={{ marginBottom: "2px", paddingLeft: "15px" }}
                    variant="subtitle1"
                  >
                    Comunicados
                  </Typography>
                </Box>
                <Box display="flex" justifyContent="center">
                  <img
                    src={newsImg}
                    alt="Imagem Aviso Atem"
                    width={"90%"}
                    style={{
                      textAlign: "center",
                      marginBottom: "20px",
                    }}
                  />
                </Box>
              </Box>
            )}
          </>
        )}

        {isMobile && (
          <Box
            width="100%"
            display="flex"
            textAlign="center"
            alignItems="center"
            justifyContent="center"
            paddingBottom="50px"
            marginTop={3}
          >
            <img alt={"logoAtem"} src={logoAtem} width={37} height={27} />
          </Box>
        )}
        {!isMobile && (
          <Modal
            open={openComunication}
            onClose={handleCloseModal}
            hiddenButton
          >
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              height="auto"
              marginTop="2%"
            >
              <Box display="flex" flexDirection="column" alignItems="center">
                <img
                  src={imgComunication?.imageUrl}
                  alt="img"
                  style={{
                    maxWidth: "90vw",
                    maxHeight: "80vh",
                    height: "auto",
                    width: "auto",
                  }}
                />
              </Box>
            </Box>
          </Modal>
        )}
      </Box>
    </Layout>
  );
};

export default HomePage;
